import React, { useCallback, useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import Form from "../../components/Form";
import PropTypes from "prop-types";
import Paper from "../../components/Paper";
import { makeStyles } from "../../utils/styles";
import Typography from "../../components/Typography";
import Divider from "../../components/Divider";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FORM, ROLES } from "../../utils/constans";
import Button from "../../components/Button";
import ButtonGroup from "../../components/ButtonGroup";
import Icon from "../../components/Icon";
import useTheme from "../../hooks/useTheme";
import { Col, Row } from "react-styled-flexboxgrid";
import Input from "../../components/Input";
import TextArea from "../../components/TextArea";
import { findByID, parseDataToOptions, parseInitialValuesJob, parseUserSuppliersToOptions, preventDefault } from "../../utils/helpers";
import Box from "../../components/Box";
import Select from "../../components/Select";
import FormLabel from "../../components/FormLabel";
import DatePicker from "../../components/DatePicker";
import IconWrap from "../../components/IconWrap";
import InputTags from "../../components/InputTags";
import { candidatesRankingsOptions } from "../../utils/form";
import { addHours, differenceInDays, fromUnixTime, getUnixTime, getHours, set, getMinutes, differenceInMinutes, add, isSameDay } from "date-fns";
import Banner from "../../components/Banner";
import { useApi } from "../../hooks/useApi";
import { ApiLanguages } from "../../api/languages";
import { toast } from "react-toastify";
import { ApiRequirements } from "../../api/requirements";
import Loader from "../../components/Loader";
import ReactDatePicker from "react-datepicker";
import FormError from "../../components/FormError";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((css) => ({
    paper: css`
        position: relative;
        overflow: hidden;
        padding: 30px 30px 15px 30px;
        width: 940px;
    `,
    dividerHeader: css`
        margin-top: 25px;
        margin-bottom: 30px;
    `,
    dividerFooter: css`
        margin-top: 30px;
        margin-bottom: 15px;
    `,
    boxLeft: css`
        margin-right: 40px;
    `,
    boxRight: css`
        margin-left: 40px;
    `,
    datePickers: css`
        display: flex;
        align-items: center;
        justify-content: space-between;
    `,
    datePickersSeparator: css`
        margin: 0 12px 20px 12px;
    `,
    info: css`
        display: flex;
        margin-bottom: 20px;
    `,
    timeSeparator: css`
        display: block;
        margin: 20px 12px 0 12px;
        span {
            display: block;
            margin: 4px 0;
        }
    `,
    icon: css`
        display: block;
    `,
    textOne: css`
        font-size: 12px;
        line-height: 16px;
        margin-left: 20px;
    `,
    noMarginBottom: css`
        margin-bottom: 0;
    `,
    inputNumber: css`
        min-width: 65px;
        margin-bottom: 0;
    `,
    banner: css`
        margin-top: 25px;
    `,
    bannerTypography: css`
        margin-bottom: 15px;
    `
}));

const schemaBasic = {
    title: yup.string().required(FORM.FIELD_IS_REQUIRED),
    profession_id: yup.string().required(FORM.FIELD_IS_REQUIRED),
    start_ranking: yup.string().required(FORM.FIELD_IS_REQUIRED),
    vacancies: yup.number().typeError(FORM.VALUE_MUST_BE_NUMBER).min(1, FORM.VALUE_WRONG).required(FORM.FIELD_IS_REQUIRED),
    time_start: yup.number().typeError(FORM.VALUE_MUST_BE_NUMBER),
    time_end: yup.number().typeError(FORM.VALUE_MUST_BE_NUMBER),
    time_offset: yup.number().typeError(FORM.VALUE_MUST_BE_NUMBER).min(0, FORM.VALUE_WRONG).max(4, FORM.VALUE_WRONG),
    requirements_ids: yup.array(),
    languages: yup.array(),
    comment: yup.string(),
    place_id: yup.string().required(FORM.FIELD_IS_REQUIRED)
};

const schemaAdministratorAndManager = yup.object().shape({
    ...schemaBasic
});

const schemaSuperAdmin = yup.object().shape({
    ...schemaBasic,
    supplier_id: yup.string().required(FORM.FIELD_IS_REQUIRED)
});

const getPreloadedValues = (date, role) => {
    const preloadedValues = {
        start_ranking: 0,
        time_start: getUnixTime(addHours(set(date, { minutes: 0, seconds: 0 }), 1)),
        time_end: getUnixTime(addHours(set(date, { minutes: 0, seconds: 0 }), 9)),
        time_offset: 0,
        requirements_ids: [],
        languages: [],
        profession_id: '',
        vacancies: 1,
        place_id: ''
    }
    if (role === ROLES.SUPERADMIN) {
        return {
            ...preloadedValues,
            supplier_id: ''
        }
    } else {
        return preloadedValues;
    }
};

const FormJob = ({ role, loading, onSubmit, onClickCancel, initialValues, edit, professions, places }) => {
    const { t } = useTranslation()
    const styles = useStyles();
    const theme = useTheme();
    const { register, handleSubmit, errors, control, watch, setValue, setError, getValues, clearErrors, reset } = useForm({
        resolver: yupResolver(role === ROLES.SUPERADMIN ? schemaSuperAdmin : schemaAdministratorAndManager),
        defaultValues: {
            ...getPreloadedValues(new Date(), role),
            ...edit ? initialValues : parseInitialValuesJob(initialValues)
        },
    });

    // Setup request
    const [{ data: languages }, getLanguages] = useApi(ApiLanguages.get);
    const [, createLanguage] = useApi(ApiLanguages.create, { manual: true });
    const [{ data: requirements }, getRequirements] = useApi(ApiRequirements.get);
    const [, createRequirement] = useApi(ApiRequirements.create, { manual: true });

    const watchStartTime = fromUnixTime(watch('time_start'));
    const watchEndTime = fromUnixTime(watch('time_end'));
    const watchRequirementsIDs = watch("requirements_ids");
    const watchLanguages = watch("languages");
    const watchProfessionID = watch("profession_id");
    const watchPlaceID = watch("place_id");

    // Memoize data
    const today = useMemo(
        () => new Date(),
        []
    );

    const professionsOptions = useMemo(
        () => professions && parseDataToOptions(professions),
        [professions]
    );

    const placesOptions = useMemo(
        () => places && parseDataToOptions(places),
        [places]
    );

    const suppliersOptions = useMemo(
        () => {
            const place = places.find(place => parseInt(place.id, 10) === parseInt(watchPlaceID, 10));
            return parseUserSuppliersToOptions(place?.user_suppliers ?? []);
        },
        [places, watchPlaceID]
    );

    const languagesSelected = useMemo(() => {
        if (!!!languages || watchLanguages.length === 0) return [];
        return languages.filter(language => !!watchLanguages.find(languageID => languageID === language.id));
    }, [languages, watchLanguages]);

    const requirementsSelected = useMemo(() => {
        if (!!!requirements || watchRequirementsIDs.length === 0) return [];
        return requirements.filter(requirement => !!watchRequirementsIDs.find(requirementID => requirementID === requirement.id));
    }, [requirements, watchRequirementsIDs]);

    const filterPassedTime = useCallback(
        time => {
            if (isSameDay(watchStartTime, time)) {
                const selectedDate = new Date(time);

                return today.getTime() < selectedDate.getTime();
            }
            return true;
        },
        [watchStartTime, today]
    );

    // Reset default values after open form if not exist initial values
    useEffect(() => {
        if (!initialValues) {
            reset(getPreloadedValues(today, role));
        }
    }, [role, initialValues, today, reset])

    useEffect(() => {
        register({ name: "time_start", valueAsDate: true, });
        register({ name: "time_end" });
        register({ name: "requirements_ids" });
        register({ name: "languages" });
        register({ name: "hourly_rate_user" });
        register({ name: "hourly_rate_admin" });
        register({ name: "place_id" });
    }, [register]);

    useEffect(() => {
        if (!!!professions) return [];
        const profession = findByID(professions, watchProfessionID);
        setValue("hourly_rate_user", profession?.hourly_rate_user);
        setValue("hourly_rate_admin", profession?.hourly_rate_admin);
    }, [professions, watchProfessionID, setValue]);

    useEffect(() => {
        const endTime = set(watchEndTime, { hours: getHours(watchStartTime), minutes: getMinutes(watchStartTime) });
        if (differenceInDays(watchStartTime, endTime) > 0) {
            const newEndTime = set(watchStartTime, { hours: getHours(watchEndTime), minutes: getMinutes(watchEndTime) });
            setValue('time_end', getUnixTime(newEndTime));
        }
        if (differenceInMinutes(watchEndTime, watchStartTime) < 0) {
            const newEndTime = add(watchEndTime, { days: 1 });
            setValue('time_end', getUnixTime(newEndTime));
        }
    }, [watchStartTime, watchEndTime, setValue]);

    const handleChangeBlurTimeOffset = e => {
        if (e.target.value.length === 0) {
            setValue('time_offset', 0);
        }
    };

    const handleClickAddLanguages = async value => {
        if (!value.error && (value.tag || value.name)) {
            const languagesIds = getValues('languages');
            if (value.tag?.id) {
                setValue("languages", [...languagesIds, value.tag.id]);
            } else {
                const res = await createLanguage(value?.name);
                if (res?.data) {
                    toast.success(t('Język został dodany do bazy'));
                    setValue("languages", [...languagesIds, res.data?.id]);
                    getLanguages();
                }
            }
            clearErrors("languages");
        } else if (value.error && value.message) {
            setError("languages", {
                type: "manual",
                message: value.message
            });
        }
    }

    const handleClickAddRequirments = async value => {
        if (!value.error && (value.tag || value.name)) {
            const requirementsIds = getValues('requirements_ids');
            if (value.tag?.id) {
                setValue("requirements_ids", [...requirementsIds, value.tag.id]);
            } else {
                const res = await createRequirement(value?.name);
                if (res?.data) {
                    toast.success(t('Dodatkowe wymaganie zostało dodane do bazy'));
                    setValue("requirements_ids", [...requirementsIds, res.data?.id]);
                    getRequirements();
                }
            }
            clearErrors("requirements_ids");
        } else if (value.error && value.message) {
            setError("requirements_ids", {
                type: "manual",
                message: value.message
            });
        }
    }

    const handleClickDelete = key => item => setValue(key, getValues(key).filter(id => id !== item.id));

    const handleChangeStartDate = date => {
        const currTimeStart = fromUnixTime(getValues('time_start'));
        const diff = differenceInMinutes(today, currTimeStart);
        if (diff < 0) {
            const nextTimeStart = add(set(date, { hours: getHours(today) }), { hours: 1 });
            setValue('time_start', getUnixTime(nextTimeStart));
        } else {
            setValue('time_start', getUnixTime(date))
        }
    };
    const handleChangeEndDate = date => {
        const currTimeStart = fromUnixTime(getValues('time_start'));
        const diff = differenceInMinutes(date, currTimeStart);
        if (diff < 0) {
            const nextTimeEnd = set(date, { hours: getHours(currTimeStart), minutes: getMinutes(currTimeStart) });
            setValue('time_end', getUnixTime(nextTimeEnd));
        } else {
            setValue('time_end', getUnixTime(date));
        }
    };

    const handleChangeTime = key => date => {
        if (date) {
            setValue(key, getUnixTime(date));
        }
    };

    return (
        <Paper
            customStyle={styles.paper}
        >
            <Form onSubmit={preventDefault}>
                <Typography
                    as="h1"
                >
                    {edit ? t("Edytuj zlecenie") : t("Nowe zlecenie")}
                </Typography>
                <Divider customStyle={styles.dividerHeader} />
                <Row>
                    <Col xs={6}>
                        <Box customStyle={styles.boxLeft}>
                            <Input
                                type="text"
                                ref={register}
                                name="title"
                                label={t("Nazwa zlecenia")}
                                placeholder={t("Nazwa zlecenia")}
                                error={!!errors.title}
                                errorText={errors.title?.message}
                            />
                            <Controller
                                control={control}
                                name="profession_id"
                                render={({ onChange, value }) => (
                                    <Select
                                        label={t("Zawód kandydatów")}
                                        placeholder={t("Wybierz")}
                                        value={value}
                                        onChange={onChange}
                                        options={professionsOptions}
                                        error={!!errors.profession_id}
                                        errorText={errors.profession_id?.message}
                                        fullWidth
                                    />
                                )}
                            />
                            <Row>
                                <Col xs={8}>
                                    <Controller
                                        control={control}
                                        name="start_ranking"
                                        render={({ onChange, value }) => (
                                            <Select
                                                label={t("Ranking kandydatów")}
                                                placeholder={t("Wybierz")}
                                                value={value}
                                                onChange={onChange}
                                                options={candidatesRankingsOptions}
                                                error={!!errors.start_ranking}
                                                errorText={errors.start_ranking?.message}
                                                fullWidth
                                            />
                                        )}
                                    />
                                </Col>
                                <Col xs={4}>
                                    <Input
                                        type="number"
                                        ref={register}
                                        name="vacancies"
                                        label={t("Liczba kandydatów")}
                                        placeholder={t("Liczba")}
                                        error={!!errors.vacancies}
                                        errorText={errors.vacancies?.message}
                                        min={1}
                                    />
                                </Col>
                            </Row>
                            <FormLabel>{t("Data świadczenia pracy od - do")}</FormLabel>
                            <Box customStyle={styles.datePickers}>
                                <DatePicker
                                    selected={watchStartTime}
                                    onChange={handleChangeStartDate}
                                    selectsStart
                                    startDate={watchStartTime}
                                    endDate={watchEndTime}
                                    minDate={today}
                                    disabled={edit}
                                />
                                <IconWrap customStyle={styles.datePickersSeparator}>
                                    <Icon name="minus" width={8} height={8} fill={theme.colors.darkGrey} />
                                </IconWrap>
                                <DatePicker
                                    selected={watchEndTime}
                                    onChange={handleChangeEndDate}
                                    selectsEnd
                                    startDate={watchStartTime}
                                    endDate={watchEndTime}
                                    minDate={watchStartTime}
                                    disabled={edit}
                                />
                            </Box>
                            <Box customStyle={styles.info}>
                                <Icon name="info" width={18} height={18} fill={theme.colors.grey} />
                                <Typography customStyle={styles.textOne} color={theme.colors.grey}>{t("W przypadku dodania  zakresu dat, utworzone zostaną zlecenia na każdy dzień osobno")}.</Typography>
                            </Box>
                            <Box customStyle={styles.datePickers}>
                                <Box>
                                    <ReactDatePicker
                                        selected={watchStartTime}
                                        onChange={handleChangeTime('time_start')}
                                        showTimeSelect
                                        showTimeSelectOnly
                                        timeFormat="HH:mm"
                                        timeIntervals={30}
                                        dateFormat="HH:mm"
                                        popperPlacement="top-start"
                                        timeCaption="godzina"
                                        locale="pl"
                                        filterTime={filterPassedTime}
                                        customInput={
                                            <Input
                                                type="text"
                                                label={t("Start zlecenia")}
                                                customStyle={styles.noMarginBottom}
                                            />
                                        }
                                    />
                                </Box>
                                <IconWrap customStyle={styles.timeSeparator}>
                                    <Icon name="minus" width={8} height={8} fill={theme.colors.darkGrey} />
                                </IconWrap>
                                <Box>
                                    <ReactDatePicker
                                        selected={watchEndTime}
                                        onChange={handleChangeTime('time_end')}
                                        showTimeSelect
                                        showTimeSelectOnly
                                        timeFormat="HH:mm"
                                        timeIntervals={30}
                                        dateFormat="HH:mm"
                                        popperPlacement="top-start"
                                        timeCaption="godzina"
                                        locale="pl"
                                        customInput={
                                            <Input
                                                type="text"
                                                label={t("Zakończenie zlecenia")}
                                                customStyle={styles.noMarginBottom}
                                            />
                                        }
                                    />
                                </Box>
                                <Box customStyle={styles.timeSeparator}>
                                    <IconWrap customStyle={styles.icon}>
                                        <Icon name="plus" width={8} height={8} fill={theme.colors.darkGrey} />
                                    </IconWrap>
                                    <IconWrap customStyle={styles.icon}>
                                        <Icon name="minus" width={8} height={2} fill={theme.colors.darkGrey} />
                                    </IconWrap>
                                </Box>
                                <Box>
                                    <Input
                                        type="number"
                                        ref={register}
                                        name="time_offset"
                                        label={t("Margines")}
                                        placeholder="h"
                                        error={!!errors.time_offset}
                                        onBlur={handleChangeBlurTimeOffset}
                                        min={0}
                                        max={4}
                                        customStyle={styles.inputNumber}
                                    />
                                </Box>
                            </Box>
                            {
                                !!errors.time_offset &&
                                <FormError>
                                    {errors.time_offset?.message}
                                </FormError>
                            }
                        </Box>
                    </Col>
                    <Col xs={6}>
                        <Box customStyle={styles.boxRight}>
                            {
                                role === ROLES.SUPERADMIN &&
                                <Controller
                                    control={control}
                                    name="place_id"
                                    render={({ onChange, value }) => (
                                        <Select
                                            label={t("Obiekt")}
                                            placeholder={t("Wybierz")}
                                            value={value}
                                            onChange={onChange}
                                            options={placesOptions}
                                            error={!!errors.place_id}
                                            errorText={errors.place_id?.message}
                                            fullWidth
                                        />
                                    )}
                                />
                            }
                            {
                                role === ROLES.SUPERADMIN &&
                                <Controller
                                    control={control}
                                    name="supplier_id"
                                    render={({ onChange, value }) => (
                                        <Select
                                            label={t("Osoba zarządzająca zgłoszeniem")}
                                            placeholder={t("Wybierz")}
                                            value={value}
                                            onChange={onChange}
                                            options={suppliersOptions}
                                            error={!!errors.supplier_id}
                                            errorText={errors.supplier_id?.message}
                                            fullWidth
                                        />
                                    )}
                                />
                            }
                            <InputTags
                                id="requirements"
                                label={t("Dodatkowe wymagania")}
                                placeholder={t("Wpisz umiejętność i naciśnij Enter")}
                                tags={requirementsSelected}
                                tagsHints={requirements}
                                useHintsToFindTag
                                isReturnNewTag
                                onClickAdd={handleClickAddRequirments}
                                onClickDelete={handleClickDelete('requirements_ids')}
                                onEnterKeyDown={handleClickAddRequirments}
                                enableEnterKeyDown
                                error={!!errors.requirements_ids}
                                errorText={errors.requirements_ids?.message}
                            />
                            <InputTags
                                id="languages"
                                label={t("Wymagany język")}
                                placeholder={t("Wpisz język i naciśnij Enter")}
                                tags={languagesSelected}
                                tagsHints={languages}
                                useHintsToFindTag
                                isReturnNewTag
                                onClickAdd={handleClickAddLanguages}
                                onClickDelete={handleClickDelete('languages')}
                                onEnterKeyDown={handleClickAddLanguages}
                                enableEnterKeyDown
                                error={!!errors.languages}
                                errorText={errors.languages?.message}
                            />
                            <TextArea
                                ref={register}
                                name="comment"
                                label={t("Dodatkowe informacje")}
                                placeholder={t("Opcjonalnie")}
                                error={!!errors.comment}
                                errorText={errors.comment?.message}
                                fullWidth
                                height={role === ROLES.SUPERADMIN ? 100 : 176}
                            />
                        </Box>
                    </Col>
                </Row>
                {
                    edit &&
                    <Banner customStyle={styles.banner}>
                        <Typography color="inherit" customStyle={styles.bannerTypography}>
                            {t("Dokonanie zmiany daty zlecenia, godzin lub wymagań dotyczących zawodu zlecenia, może skutkować inną niż deklarowana dostępnością kandydatów. Kandydatom, którzy nie będą w stanie podjąć pracy w zmienionych warunkach, zostanie wysłane powiadomienie o anulowaniu zlecenia przez pracodawcę.")}
                        </Typography>
                        <Input
                            type="text"
                            ref={register}
                            name="reason"
                            placeholder={t("Powód zmiany (opcjonalnie)")}
                            error={!!errors.reason}
                            errorText={errors.reason?.message}
                            noBorder
                        />
                    </Banner>
                }
                <Divider customStyle={styles.dividerFooter} />
                <Row end="xs">
                    <Col>
                        <ButtonGroup>
                            <Button
                                variant="text"
                                onClick={onClickCancel}
                                startIcon={
                                    <Icon
                                        name="close"
                                        width={12}
                                        height={12}
                                        fill={theme.colors.purple}
                                    />
                                }
                            >
                                {t("ANULUJ")}
                            </Button>
                            <Button
                                onClick={handleSubmit(onSubmit)}
                                startIcon={edit ?
                                    <Icon name="order" width={12} height={12} fill={theme.colors.white} /> :
                                    <Icon name="save" width={14} height={14} fill={theme.colors.white} />
                                }
                                disabled={loading}
                            >
                                {edit ? t("PUBLIKUJ ZMIANY") : t("ZAPISZ")}
                            </Button>
                        </ButtonGroup>
                    </Col>
                </Row>
            </Form>
            <Loader show={loading} />
        </Paper>
    );
};

FormJob.propTypes = {
    loading: PropTypes.bool,
    onSubmit: PropTypes.func,
    onClickCancel: PropTypes.func,
    edit: PropTypes.bool,
    initialValues: PropTypes.object,
};

export default FormJob;
